import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import type { BaseCategory } from "~/types/baseCategory";
import type { ICategory } from "~/types/category";
import { buildCategoryColorMap } from "~/utils/generateColor";

interface ICategoriesProviderProps {
  children: ReactNode;
}

type CategoryColorMap = Record<string, string>;

const DEFAULT_CATEGORY_COLOR = "#1976d2";

interface ICategoriesContextType {
  categories: ICategory[];
  setCategories: (categories: ICategory[]) => void;
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
  getCategoryNameById: (id: string) => string | undefined;
  getCategorySlugById: (id: string) => string | BaseCategory;
  getCategoryIdBySlug: (slug: string) => string;
  categoryColors: CategoryColorMap;
  getCategoryColorById: (id: string) => string;
}

const CategoriesContext = createContext<ICategoriesContextType>({
  categories: [],
  setCategories: () => {},
  isLoading: false,
  setIsLoading: () => {},
  getCategoryNameById: () => undefined,
  getCategorySlugById: () => "",
  getCategoryIdBySlug: () => "",
  categoryColors: {},
  getCategoryColorById: () => "",
});

export const CategoriesProvider = ({ children }: ICategoriesProviderProps) => {
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [categoryColors, setCategoryColors] = useState<CategoryColorMap>({});

  const setCategoriesData = useCallback((categories: ICategory[]) => {
    setCategories(categories);
    setCategoryColors((prev) => buildCategoryColorMap(categories, prev));
  }, []);

  const setLoadingData = useCallback((value: boolean) => {
    setIsLoading(value);
  }, []);

  const getCategoryNameById = useCallback(
    (id: string) => {
      return categories.find((category) => category.id === id)?.name;
    },
    [categories]
  );

  const getCategorySlugById = useCallback(
    (id: string) => {
      return categories.find((category) => category.id === id)?.slug || "";
    },
    [categories]
  );

  const getCategoryIdBySlug = useCallback(
    (slug: string) => {
      return categories.find((category) => category.slug === slug)?.id || "";
    },
    [categories]
  );

  const getCategoryColorById = useCallback(
    (id: string) => categoryColors[id] ?? DEFAULT_CATEGORY_COLOR,
    [categoryColors]
  );

  const value = useMemo(
    () => ({
      categories,
      setCategories: setCategoriesData,
      isLoading,
      setIsLoading: setLoadingData,
      getCategoryNameById,
      getCategorySlugById,
      getCategoryIdBySlug,
      categoryColors,
      getCategoryColorById,
    }),
    [
      categories,
      setCategoriesData,
      isLoading,
      setLoadingData,
      getCategoryNameById,
      getCategorySlugById,
      getCategoryIdBySlug,
      categoryColors,
      getCategoryColorById,
    ]
  );

  return (
    <CategoriesContext.Provider value={value}>
      {children}
    </CategoriesContext.Provider>
  );
};

export const useCategories = (): ICategoriesContextType => {
  const context = useContext(CategoriesContext);

  if (!context) {
    throw new Error("useCategories must be used within a CategoriesProvider");
  }

  return context;
};
